<template>
    <section
        class="photography-video-gallery bg-black-700 full-width tablet:min-h-screen flex flex-col tabletLandscape:flex-row relative">
        <!--Gallery-->
        <div class="video product-gallery h-screen relative flex flex-col align-center">
            <!--Slide Image Background-->
            <transition name="fadeBg">
                <div
                    :key="currentSlide"
                    class="overflow-hidden bg-black-700 h-full">
                    <video
                        id="videoGallery"
                        :autoplay="false"
                        :class="{'blur-video': scrubDragging}"
                        class="tis-video object-cover h-full"
                        preload
                    >
                        <source
                            id="source"
                            :src="this.currentSlideContentElement('src')"
                            type="video/mp4"
                        >
                    </video>
                </div>
            </transition>

            <!--Gallery Thumbnail Controls-->
            <!--Unmasked Wrapper-->
            <div class="unmasked-thumbnail-wrapper absolute bottom-0 flex flex-col self-center items-center">
                <!-- Current Image Label-->
                <transition name="fade">
                    <div
                        :key="currentSlide"
                        class="thumb-label transition-all text-sm uppercase duration-200 bg-black-700 px-8 py-1 transition-all absolute">
                        <span
                            v-if="currentSlideContentElement('pre_title')"
                            class="text-gold-500 font-bold mr-1">{{ currentSlideContentElement('pre_title') }}</span>
                        <span
                            v-if="currentSlideContentElement('title')"
                            class="font-bold text-gray-450">{{ currentSlideContentElement('title') }}</span>
                    </div>
                </transition>

                <transition name="fade">
                    <div
                        :key="currentSlide"
                        class="video-controls uppercase transition-all text-sm duration-200 bg-black-700 px-8 py-2 transition-all duration-200 absolute">
                        <div class="flex items-center">
                            <div
                                class="z-40"
                                @click="handlePlay">
                                <svg-icon
                                    v-show="!playing"
                                    class="arrow-left mr-3 text-white transition-all duration-200 w-2 h-6 hover:text-gold-500 cursor-pointer"
                                    icon="video-play"
                                />
                            </div>
                            <div
                                class="z-40"
                                @click="handlePause">
                                <svg-icon
                                    v-show="playing"
                                    class="arrow-left mr-3 text-white transition-all duration-200 w-2 h-6 hover:text-gold-500 cursor-pointer"
                                    icon="video-pause"
                                />
                            </div>

                            <vue-slider
                                v-model="scrub"
                                :contained="true"
                                :height="3"
                                :tooltip="'none'"
                                :width="127"
                                @change="scrubTo"
                                @drag-end="scrubDragging = false"
                                @drag-start="scrubDragging = true"
                            />
                        </div>
                    </div>
                </transition>

                <!--Masked Container-->
                <div
                    class="gallery gallery-controls tablet:px-12 w-full  flex flex-col items-center align-center phone:mb-12 mb-6"
                >
                    <!--Row for Gallery and arrows-->
                    <div class="flex align-center items-center">
                        <!--Previous Group Arrow-->
                        <transition name="fade">
                            <span
                                v-show="thumbnailsCarouselPosition > 0"
                                class="mt-0 absolute mt-8 left-0"
                                @click="prevGallerySet">
                                <svg-icon
                                    class="arrow-left transition-all duration-200 phone:hidden text-gold-500 w-8 transform rotate-180 h-4 mr-3 hover:rotate-180 cursor-pointer"
                                    icon="arrow-horizontal"
                                />
                            </span>
                        </transition>
                        <!--Masked Thumbnails iterator-->

                        <div class="gallery-thumbnails flex items-center overflow-hidden justify-center">
                            <ul
                                ref="thumbnailGallery"
                                v-touch:swipe.left="nextGallerySet"
                                v-touch:swipe.right="prevGallerySet"
                                class="flex transition-all duration-500 ease-in-out flex items-center"
                                @keyup.left="prevSlideItem"
                                @keyup.right="nextSlideItem"
                            >
                                <li
                                    v-for="(thumbItem, thumbIndex) in sectionVideoGallery.videoGallery"
                                    :key="thumbIndex"
                                    :class="{'border-4 border-gold-500': thumbIndex === currentSlide}"
                                    class="gallery-thumb-item flex-shrink-0 transition-all duration-200 relative"
                                >
                                    <img
                                        :src="thumbItem.thumbnail"
                                        alt=""
                                        class="cursor-pointer"
                                        height="69"
                                        width="100"
                                        @click="setCurrentSlide(thumbIndex)">
                                </li>
                            </ul>
                        </div>
                        <!--Next Group Arrow-->
                        <transition name="fade">
                            <span
                                v-show="sectionVideoGallery.videoGallery.length > 3"
                                class="mt-8 absolute right-0"
                                @click="nextGallerySet">
                                <svg-icon
                                    class="arrow-right phone:hidden w-8 h-4 ml-3 transition-all duration-200 text-gold-500 cursor-pointer"
                                    icon="arrow-horizontal"
                                />
                            </span>
                        </transition>
                    </div>

                    <!--Group Dots-->
                    <div
                        v-if="showGroupDots"
                        class="dots mt-4">
                        <ul class="list-square flex text-4xl list-inside leading-none transition-all">
                            <li
                                v-for="(dot, index) in galleryGroupsCount"
                                :class="{'text-gold-500': index === thumbnailsCarouselPosition}"
                                class="w-4 text-white cursor-pointer hover:scale-110"
                                @click="goToMarker(index)"
                            />
                        </ul>
                    </div>
                    <!--Instructions-->
                    <p
                        class="w-full mt-4 text-center font-bold  tablet:hidden uppercase text-white cursor-pointer"
                        @click="nextGallerySet"
                        v-text="sectionVideoGallery.gallerySwipeInstructions" />
                </div>
            </div>
        </div>
        <!--Product Description-->
        <div
            class="product-description max-w-3xl bg-black-700 flex flex-col mobile:self-center phone:-mt-10 tablet:-mt-2 bg-black text-white tabletLandscape:min-h-screen tabletLandscape:mt-0 mobile:mx-4 pl-16 pr-8 py-12 tablet:pl-24 tablet:px-12 tablet:pt-24 tabletLandscape:px-8 tabletLandscape:pl-16 tabletLandscape:pt-8 desktop:pt-18 tablet:mt-0 z-10">
            <svg-icon
                class="text-black w-36 h-28"
                icon="videoServiceInverted" />

            <h3
                class="uppercase mb-6 text-xl tablet:text-2xl font-bold text-grey-800 border-t border-gold-500 pt-8 mt-8"
                v-html="sectionVideoGallery.products[currentProduct].productDescription.productTitle" />
            <p
                class="flex-grow text-sm tablet:text-lg tabletLandscape:text-sm desktop:text-md hd:text-lg leading-relaxed mb-8"
                v-html="sectionVideoGallery.products[currentProduct].productDescription.productContent" />
            <ul class="list-square">
                <li
                    class="border-t border-gold-500 pt-4 font-bold text-gray-700 text-xs tablet:text-lg tabletLandscape:text-sm desktop:text-md hd:text-lg leading-relaxed mb-8"
                    v-html="sectionVideoGallery.products[currentProduct].productDescription.productNote" />
            </ul>
        </div>
    </section>
</template>

<script>
import SvgIcon from '@whisper/components/icons/SvgIcon';
import VueSlider from 'vue-slider-component';
import isEmpty from 'lodash/isEmpty';
import Vue from 'vue';

export default {
  name: 'SectionPhotographyVideoGallery',
  components: {
    SvgIcon,
    VueSlider,
  },
  props: {
    sectionVideoGallery: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    transitioning: false,
    scrub: 0,
    scrubDragging: false,
    vid: null,
    playing: false,
    galleryItemWidth: 104,
    currentProduct: 'photography_video',
    currentSlide: 0,
    currentFeature: 0,
    thumbnailsCarouselPosition: 0,
    galleryVisibleItems: 5,
    window: {
      width: 0,
      height: 0,
    },
  }),
  computed: {
    showGroupDots() {
      return this.galleryGroupsCount > 1;
    },
    isHdWidth() {
      if (this.window.width < 1920) {
        return false;
      }
      return true;
    },
    galleryGroupsCount() {
      return Math.ceil(this.sectionVideoGallery.videoGallery.length / this.galleryVisibleItems);
    },
    currentVideoName() {
      const preTitle = this.currentSlideContentElement('pre_title');
      const title = this.currentSlideContentElement('title');

      if (!isEmpty(preTitle) && !isEmpty(title)) {
        return `${preTitle} ${title}`;
      }

      if (!isEmpty(preTitle)) {
        return preTitle;
      }

      if (!isEmpty(title)) {
        return title;
      }

      return `Video #${this.currentSlide}`;
    },
  },
  created() {
    window.addEventListener('resize', () => this.handleViewPortResize);
    this.handleViewPortResize();
  },
  mounted() {
    this.vid = document.getElementById('videoGallery');
  },
  methods: {
    updateScrubber() {
      this.scrub = this.vid.currentTime / this.vid.duration * 100;
    },
    scrubTo() {
      this.vid.currentTime = this.scrub / 100 * this.vid.duration;
      this.playing = true;
      this.vid.play();
    },
    async handlePlay() {
      this.vid = document.getElementById('videoGallery');
      this.vid.ontimeupdate = () => this.updateScrubber();
      try {
        await this.vid.play();
        this.playing = true;
        if (Vue.analytics && Vue.analytics.trackWebsiteEvent) {
          Vue.analytics.trackWebsiteEvent(
            'Played Video',
            ['mixpanel'],
            {
              'Video Name': this.currentVideoName,
            },
          );
        }
      } catch (err) {
        this.playing = false;
      }
    },
    handlePause() {
      this.playing = false;
      if (Vue.analytics && Vue.analytics.trackWebsiteEvent) {
        Vue.analytics.trackWebsiteEvent(
          'Paused Video',
          ['mixpanel'],
          {
            'Video Name': this.currentVideoName,
            '% Watched': this.scrub,
          },
        );
      }
      this.vid.pause();
    },

    videoCanPlay() {
      return !!this.$refs.video.canPlayType;
    },

    setImageUrl() {
      if (this.img) {
        this.$el.style.backgroundImage = `url(${this.img})`;
      }
    },

    getMediaType(src) {
      return `video/${src.split('.').pop()}`;
    },

    getfullCarouselWidth() {
      // Computed based on number of items x their width
      return this.sectionVideoGallery.videoGallery.length * this.galleryItemWidth;
    },
    setActiveAccordionPane(optionIndex) {
      if (this.currentFeature === optionIndex) {
        this.currentFeature = null;
      }
      this.currentFeature = optionIndex;
    },
    beforeEnter(el) {
      el.style.height = '5';
    },
    enter(el) {
      el.style.height = `${el.scrollHeight}px`;
    },
    beforeLeave(el) {
      el.style.height = `${el.scrollHeight}px`;
    },
    leave(el) {
      el.style.height = '0';
    },
    setCarouselPosition(marker) {
      if (marker < 0) {
        return;
      }
      this.thumbnailsCarouselPosition = marker;
      const galleryEl = this.$refs.thumbnailGallery;
      const position = marker * this.galleryVisibleItems * this.galleryItemWidth * -1;
      galleryEl.setAttribute('style', `transform: translateX(${position}px)`);
    },
    setCurrentProductFromCurrentSlide() {
      this.currentProduct = this.sectionVideoGallery.videoGallery[this.currentSlide].productId;
    },
    prevGallerySet() {
      if (this.thumbnailsCarouselPosition - 1 > 0) {
        this.goToMarker(this.thumbnailsCarouselPosition - 1);
        return;
      }
      this.goToMarker(0);
    },
    nextGallerySet() {
      if (this.thumbnailsCarouselPosition + 1 < this.galleryGroupsCount) {
        this.goToMarker(this.thumbnailsCarouselPosition + 1);
        return;
      }
      this.goToMarker(0);
    },
    prevSlideItem() {
      this.currentSlide--;
    },
    nextSlideItem() {
      this.currentSlide++;
    },
    setCurrentSlide(thumbIndex) {
      this.handlePause();
      this.vid = document.getElementById('videoGallery');

      this.vid.currentTime = 0;
      this.currentSlide = thumbIndex;

      setTimeout(() => {
        this.handlePlay();
      }, 1100);
    },
    goToMarker(marker) {
      this.thumbnailsCarouselPosition = marker;
      this.setCarouselPosition(marker);
    },
    handleViewPortResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
      if (this.window.width < 768) {
        this.galleryVisibleItems = 3;
      }
      if (this.window.width >= 768 && this.window.width < 1024) {
        this.galleryVisibleItems = 5;
      } else if (this.window.width >= 1024 && this.window.width < 1279) {
        this.galleryVisibleItems = 3;
      } else if (this.window.width >= 1280 && this.window.width < 1920) {
        this.galleryVisibleItems = 5;
      } else if (this.window.width > 1920) {
        this.galleryVisibleItems = 8;
      }
    },
    currentSlideContentElement(content) {
      return this.sectionVideoGallery.videoGallery[this.currentSlide][content];
    },
  },
};
</script>

<style lang="scss" scoped>
    .video-controls {
        top: -5rem;
    }

    .photography-video-gallery {

        .blur-video {
            filter: blur(4px);
        }
        .vue-slider-disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        /* rail style */
        .vue-slider-rail {
            background-color: #979797;
            border-radius: 0px;
        }

        /* process style */
        .vue-slider-process {
            background-color: #BF9C5F;
            border-radius: 0px;
        }

        /* mark style */
        .vue-slider-mark {
            z-index: 4;
        }
        .vue-slider-mark:first-child .vue-slider-mark-step, .vue-slider-mark:last-child .vue-slider-mark-step {
            display: none;
        }
        .vue-slider-mark-step {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background-color: rgba(0, 0, 0, 0.16);
        }
        .vue-slider-mark-label {
            font-size: 14px;
            white-space: nowrap;
        }
        /* dot style */
        .vue-slider-dot-handle {
            cursor: pointer;
            width: 5px;
            height: 14px;
            border-radius: 0;
            background-color: #fff;
            box-sizing: border-box;
            box-shadow: 0.5px 0.5px 2px 1px rgba(0, 0, 0, 0.32);
        }
        .vue-slider-dot-handle-focus {
            box-shadow: 0px 0px 1px 2px #BF9C5F;
        }

        .vue-slider-dot-handle-disabled {
            cursor: not-allowed;
            background-color: #ccc;
        }

        .vue-slider-dot-tooltip-inner {
            font-size: 14px;
            white-space: nowrap;
            padding: 2px 5px;
            min-width: 20px;
            text-align: center;
            color: #fff;
            border-radius: 5px;
            border-color: #BF9C5F;
            background-color: #BF9C5F;
            box-sizing: content-box;
        }
        .vue-slider-dot-tooltip-inner::after {
            content: "";
            position: absolute;
        }
        .vue-slider-dot-tooltip-inner-top::after {
            top: 100%;
            left: 50%;
            transform: translate(-50%, 0);
            height: 0;
            width: 0;
            border-color: transparent;
            border-style: solid;
            border-width: 5px;
            border-top-color: inherit;
        }
        .vue-slider-dot-tooltip-inner-bottom::after {
            bottom: 100%;
            left: 50%;
            transform: translate(-50%, 0);
            height: 0;
            width: 0;
            border-color: transparent;
            border-style: solid;
            border-width: 5px;
            border-bottom-color: inherit;
        }
        .vue-slider-dot-tooltip-inner-left::after {
            left: 100%;
            top: 50%;
            transform: translate(0, -50%);
            height: 0;
            width: 0;
            border-color: transparent;
            border-style: solid;
            border-width: 5px;
            border-left-color: inherit;
        }
        .vue-slider-dot-tooltip-inner-right::after {
            right: 100%;
            top: 50%;
            transform: translate(0, -50%);
            height: 0;
            width: 0;
            border-color: transparent;
            border-style: solid;
            border-width: 5px;
            border-right-color: inherit;
        }

        .vue-slider-dot-tooltip-wrapper {
            opacity: 0;
            transition: all 0.3s;
        }
        .vue-slider-dot-tooltip-wrapper-show {
            opacity: 1;
        }

    }
</style>
